import { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Input, message, Modal as AntModal } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { Button, MinimalPagination } from "../../../../shared";
import RebetLoader from "../../../../shared/RebetLoader/RebetLoader";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formatString } from "../../../../../utils/constants/stringUtils";
import {
  fetchAleaGamesCatalog,
  type JackpotCatalogGame,
} from "../../../../../queries/casino-configuration/features/useJackpotConfig";
import {
  jackpotGameKey,
  parseJackpotAmount,
  type JackpotGameRow,
} from "../jackpotConfig";

import "./AddJackpotGameModal.css";

const CATALOG_PAGE_SIZE = 50;

export type AddJackpotGameModalProps = {
  open: boolean;
  onClose: () => void;
  /** Game keys already on the rail (`provider::gameId`). */
  railKeys: Set<string>;
  /** Persist the game (POST). Resolves on success; reject to keep the modal open. */
  onConfirm: (game: JackpotGameRow) => void | Promise<void>;
  /** Platform currency symbol; defaults to `"$"`. */
  currencySymbol?: string;
};

type Step = 1 | 2;

/**
 * Two-step modal: pick an Alea game, then set its baseline amount.
 * Confirm calls `onConfirm` (POST /jackpot/games via parent) before closing.
 * Catalog rows and paging match Collections Add Games (checkbox + MinimalPagination).
 */
function AddJackpotGameModal({
  open,
  onClose,
  railKeys,
  onConfirm,
  currencySymbol = "$",
}: AddJackpotGameModalProps) {
  const [step, setStep] = useState<Step>(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<JackpotCatalogGame[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState<JackpotCatalogGame | null>(null);
  const [amountRaw, setAmountRaw] = useState("");
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
    setItems([]);
    setTotal(0);
    setLoading(true);
    setSelected(null);
    setAmountRaw("");
    setSubmitting(false);
    const t = window.setTimeout(() => searchRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      // Batch with setPage so React 18 only re-renders once — avoids a
      // cancelled catalog fetch of (newSearch, oldPage) when already on page > 1.
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const result = await fetchAleaGamesCatalog({
          search: debouncedSearch,
          page,
          pageSize: CATALOG_PAGE_SIZE,
        });
        if (cancelled) return;
        setItems(result.items);
        setTotal(result.total);
      } catch (err) {
        if (!cancelled) {
          message.error(
            (err as Error)?.message ||
              AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_CATALOG_ERROR,
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, debouncedSearch, page]);

  const amount = useMemo(() => parseJackpotAmount(amountRaw), [amountRaw]);
  const amountValid = amount !== null;
  const showPagination = total > CATALOG_PAGE_SIZE;

  const toggleSelect = (game: JackpotCatalogGame) => {
    const key = jackpotGameKey({ provider: game.provider, gameId: game.gameId });
    if (railKeys.has(key)) return;
    setSelected((prev) =>
      prev &&
      prev.provider === game.provider &&
      prev.gameId === game.gameId
        ? null
        : game,
    );
  };

  const handlePrimary = async () => {
    if (step === 1) {
      if (!selected) return;
      setStep(2);
      setAmountRaw("");
      return;
    }
    if (!selected || !amountValid || amount === null || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm({
        provider: selected.provider,
        gameId: selected.gameId,
        name: selected.name,
        image: selected.image,
        amount,
        isActive: selected.isActive,
        pending: false,
      });
      onClose();
    } catch {
      // Parent surfaces the error toast; keep the modal open for retry.
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => setStep(1);

  const statusLabel = (game: JackpotCatalogGame, already: boolean) => {
    if (already) return AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_ADDED;
    return game.isActive
      ? AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_ACTIVE
      : AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_INACTIVE;
  };

  const statusClass = (game: JackpotCatalogGame, already: boolean) => {
    if (already) return "jackpot-status-pill--added";
    return game.isActive
      ? "jackpot-status-pill--active"
      : "jackpot-status-pill--inactive";
  };

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      title={
        <div>
          <div className="jackpot-add-title">
            {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_ADD_MODAL_TITLE}
          </div>
          {step === 1 ? (
            <div
              className="jackpot-add-selected"
              data-testid="jackpot-catalog-selected-count"
            >
              {formatString(AppStrings.CASINO_CONFIG_GAMES_BULK_SELECTED, {
                count: String(selected ? 1 : 0),
              })}
            </div>
          ) : null}
        </div>
      }
      footer={null}
      width={560}
      destroyOnHidden
      className="jackpot-add-modal"
      data-testid="jackpot-add-modal"
    >
      {step === 1 ? (
        <p className="jackpot-add-modal-subtitle">
          {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_ADD_STEP1_SUBTITLE}
        </p>
      ) : (
        <p className="jackpot-add-modal-subtitle">
          {formatString(
            AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_ADD_STEP2_SUBTITLE,
            { name: selected?.name ?? "" },
          )}
        </p>
      )}

      {step === 1 ? (
        <>
          <Input
            ref={searchRef as never}
            allowClear
            prefix={<SearchOutlined />}
            placeholder={AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_SEARCH_CATALOG}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="jackpot-catalog-search"
          />
          <p className="jackpot-add-modal-helper">
            {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_ALEA_HELPER}
          </p>
          <div
            className="jackpot-catalog-list"
            data-testid="jackpot-catalog-list"
            role="listbox"
            aria-label={AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_ADD_MODAL_TITLE}
          >
            {loading ? (
              <div
                className="jackpot-catalog-loader"
                data-testid="jackpot-catalog-loader"
              >
                <RebetLoader height="48px" width="48px" />
              </div>
            ) : items.length === 0 ? (
              <div className="jackpot-catalog-empty">
                {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_NO_MATCH}
              </div>
            ) : (
              items.map((game) => {
                const key = jackpotGameKey({
                  provider: game.provider,
                  gameId: game.gameId,
                });
                const already = railKeys.has(key);
                const isSelected =
                  selected?.provider === game.provider &&
                  selected?.gameId === game.gameId;
                return (
                  <div
                    key={key}
                    role="option"
                    tabIndex={already ? -1 : 0}
                    aria-selected={isSelected}
                    aria-disabled={already}
                    className={`jackpot-catalog-row${
                      isSelected ? " jackpot-catalog-row--selected" : ""
                    }${already ? " jackpot-catalog-row--added" : ""}`}
                    onClick={() => toggleSelect(game)}
                    onKeyDown={(e) => {
                      if (already) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleSelect(game);
                      }
                    }}
                    data-testid={`jackpot-catalog-row-${game.gameId}`}
                  >
                    <span className="jackpot-catalog-row-main">
                      <span
                        className="jackpot-catalog-row-name"
                        title={game.name}
                      >
                        {game.name}
                      </span>
                      <span
                        className="jackpot-catalog-row-id"
                        title={`${game.provider}/${game.gameId}`}
                      >
                        {game.provider}/{game.gameId}
                      </span>
                    </span>
                    <span
                      className={`jackpot-status-pill ${statusClass(game, already)}`}
                    >
                      {statusLabel(game, already)}
                    </span>
                    <Checkbox
                      checked={isSelected}
                      disabled={already}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(game)}
                      className="jackpot-catalog-checkbox"
                      data-testid={`jackpot-catalog-checkbox-${game.gameId}`}
                      aria-hidden="true"
                      tabIndex={-1}
                    />
                  </div>
                );
              })
            )}
          </div>
          {showPagination ? (
            <div
              className="jackpot-catalog-pagination"
              data-testid="jackpot-catalog-pagination"
            >
              <MinimalPagination
                total={total}
                pageSize={CATALOG_PAGE_SIZE}
                currentPage={page}
                onPageChange={setPage}
                itemLabel={AppStrings.CASINO_CONFIG_COLLECTIONS_GAMES_SUFFIX}
              />
            </div>
          ) : null}
        </>
      ) : (
        <div className="jackpot-amount-step">
          <div className="jackpot-selected-card">
            <div className="jackpot-selected-name">{selected?.name}</div>
            <div className="jackpot-selected-id">
              {selected?.provider}/{selected?.gameId}
            </div>
          </div>
          <label className="jackpot-amount-label" htmlFor="jackpot-amount-input">
            {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_AMOUNT_LABEL}
          </label>
          <Input
            id="jackpot-amount-input"
            type="number"
            min={0.01}
            step={0.01}
            prefix={currencySymbol}
            value={amountRaw}
            onChange={(e) => setAmountRaw(e.target.value)}
            data-testid="jackpot-amount-input"
          />
          <p className="jackpot-add-modal-helper">
            {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_AMOUNT_HELPER}
          </p>
        </div>
      )}

      <div className="jackpot-add-modal-footer">
        {step === 2 && !amountValid ? (
          <span
            className="jackpot-amount-error"
            data-testid="jackpot-amount-error"
          >
            {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_AMOUNT_ERROR}
          </span>
        ) : (
          <span />
        )}
        <div className="jackpot-add-modal-actions">
          {step === 2 ? (
            <button
              type="button"
              className="jackpot-text-btn"
              onClick={handleBack}
              data-testid="jackpot-add-back"
            >
              {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_BACK}
            </button>
          ) : null}
          <button
            type="button"
            className="jackpot-text-btn"
            onClick={onClose}
            data-testid="jackpot-add-cancel"
          >
            {AppStrings.CASINO_CONFIG_FEATURES_CONFIRM_CANCEL}
          </button>
          <Button
            intent="primary"
            onClick={handlePrimary}
            disabled={
              step === 1 ? !selected : !amountValid || submitting
            }
            loading={step === 2 && submitting}
            data-testid="jackpot-add-confirm"
          >
            {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_ADD_TO_JACKPOT}
          </Button>
        </div>
      </div>
    </AntModal>
  );
}

export default AddJackpotGameModal;
